/**
 * Weather Service — fetches METAR/TAF and NOTAM data from the backend
 */
import { API_URL } from "./api";

function getHeaders(): Record<string, string> {
    const token = localStorage.getItem("token");
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

export interface MetarTafResult {
    status: string;
    file_path: string;
    filename: string;
    metar_count: number;
    taf_count: number;
    total_chunks: number;
    raw_size_kb: number;
    icao: string;
    start_date: string;
    end_date: string;
}

export interface NotamsResult {
    status: string;
    notam_count: number;
    active_count: number;
    archive_count: number;
    categories: Record<string, number>;
    errors: string[];
    icao_codes: string[];
    start_date: string;
    end_date: string;
}

/**
 * Fetch METAR/TAF data from IEM via backend
 */
export async function fetchMetarTaf(
    icao: string,
    startDate: string,
    endDate: string
): Promise<MetarTafResult> {
    const response = await fetch(`${API_URL}/weather/fetch-metar`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
            icao,
            start_date: startDate,
            end_date: endDate,
        }),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Error: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Fetch NOTAMs from Notamify via backend
 */
export async function fetchNotams(
    icaoCodes: string[],
    startDate: string,
    endDate: string,
    includeArchive: boolean = false
): Promise<NotamsResult> {
    const response = await fetch(`${API_URL}/weather/fetch-notams`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
            icao_codes: icaoCodes,
            start_date: startDate,
            end_date: endDate,
            include_archive: includeArchive,
        }),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Error: ${response.statusText}`);
    }
    return response.json();
}

// ============================================
// SIMULATION WEATHER TYPES
// ============================================

export interface WeatherConditions {
    wind_speed_mean: number;
    wind_speed_max: number;
    wind_gust_max: number | null;
    wind_direction_dominant: number | null;
    visibility_min: number;
    visibility_mean: number;
    ceiling_min: number | null;
    temperature_min: number;
    temperature_max: number;
    phenomena: string[];
    metar_count: number;
    category: 'VMC' | 'IMC' | 'LVP';
}

export interface DayConditionsResponse {
    icao: string;
    date: string;
    conditions: WeatherConditions;
    wcf: number;
    simulation_parameters: Record<string, any>;
    data_available: boolean;
    message: string | null;
}

export interface SimulationWeatherParams {
    simulation_parameters: Record<string, any>;
    wcf: number;
    source: 'metar_auto' | 'manual_override';
}

// ============================================
// SIMULATION WEATHER FUNCTIONS
// ============================================

/**
 * Get weather conditions for a specific day from parsed METAR data
 */
export async function getDayConditions(
    icao: string,
    targetDate: string
): Promise<DayConditionsResponse> {
    const response = await fetch(`${API_URL}/weather/day-conditions`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
            icao,
            target_date: targetDate,
        }),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Error: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Get recommended simulation parameters from conditions
 */
export async function getRecommendedParams(
    conditions: WeatherConditions,
    wcfOverride?: number
): Promise<SimulationWeatherParams> {
    const response = await fetch(`${API_URL}/weather/recommend-params`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
            conditions,
            wcf_override: wcfOverride ?? null,
        }),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Error: ${response.statusText}`);
    }
    return response.json();
}
